import React, { forwardRef, useState } from 'react';
import { colors, borderRadius, typography, spacing, transition } from '../tokens';
import { useTheme } from '../theme';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
  sm: { fontSize: typography.fontSize.sm, padding: `${spacing.xs} ${spacing.sm}`, minHeight: '32px' },
  md: { fontSize: typography.fontSize.base, padding: `${spacing.sm} ${spacing.md}`, minHeight: '40px' },
  lg: { fontSize: typography.fontSize.lg, padding: `${spacing.sm} ${spacing.lg}`, minHeight: '48px' },
};

type VariantTheme = { base: React.CSSProperties; hover: React.CSSProperties; active: React.CSSProperties };

function getVariantStyles(variant: ButtonVariant, dark: boolean): VariantTheme {
  switch (variant) {
    case 'primary':
      return dark
        ? {
            base: { backgroundColor: colors.primary.yellow, color: colors.neutral.black },
            hover: { backgroundColor: colors.primary.yellowDark },
            active: { backgroundColor: colors.primary.yellowDark, opacity: 0.9 },
          }
        : {
            // On light bg: charcoal button — yellow is for dark surfaces only
            base: { backgroundColor: colors.neutral.black, color: colors.neutral.white },
            hover: { backgroundColor: colors.neutral.charcoal },
            active: { backgroundColor: colors.neutral.gray800 },
          };
    case 'secondary':
      return dark
        ? {
            base: { backgroundColor: 'transparent', color: colors.primary.yellow, border: `1.5px solid ${colors.primary.yellow}` },
            hover: { backgroundColor: 'rgba(247,208,0,0.12)' },
            active: { backgroundColor: 'rgba(247,208,0,0.2)' },
          }
        : {
            base: { backgroundColor: 'transparent', color: colors.neutral.black, border: `1.5px solid ${colors.neutral.black}` },
            hover: { backgroundColor: colors.neutral.gray100 },
            active: { backgroundColor: colors.neutral.gray200 },
          };
    case 'ghost':
      return dark
        ? {
            base: { backgroundColor: 'transparent', color: colors.neutral.gray400 },
            hover: { color: colors.primary.yellow, backgroundColor: 'rgba(255,255,255,0.08)' },
            active: { backgroundColor: 'rgba(255,255,255,0.12)' },
          }
        : {
            base: { backgroundColor: 'transparent', color: colors.neutral.gray800 },
            hover: { backgroundColor: colors.neutral.gray100, color: colors.neutral.black },
            active: { backgroundColor: colors.neutral.gray200 },
          };
    case 'danger':
      return {
        base: { backgroundColor: colors.semantic.error, color: colors.neutral.white },
        hover: { backgroundColor: colors.semantic.errorDark },
        active: { backgroundColor: colors.semantic.errorDark, opacity: 0.9 },
      };
  }
}

const focusRing: React.CSSProperties = {
  outline: `3px solid ${colors.primary.yellow}`,
  outlineOffset: '2px',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, fullWidth = false, disabled, children, style, ...rest },
  ref,
) {
  const { dark } = useTheme();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pressed, setPressed] = useState(false);

  const vStyles = getVariantStyles(variant, dark);
  const isDisabled = disabled || loading;

  const computedStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    fontFamily: typography.fontFamily.body,
    fontWeight: typography.fontWeight.medium,
    borderRadius: borderRadius.md,
    border: 'none',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: transition.base,
    whiteSpace: 'nowrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    textDecoration: 'none',
    userSelect: 'none',
    width: fullWidth ? '100%' : undefined,
    opacity: isDisabled && !loading ? 0.45 : 1,
    ...sizeStyles[size],
    ...vStyles.base,
    ...(hovered && !isDisabled ? vStyles.hover : {}),
    ...(pressed && !isDisabled ? vStyles.active : {}),
    ...(focused ? focusRing : {}),
    ...style,
  };

  return (
    <button
      ref={ref}
      disabled={isDisabled}
      style={computedStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      aria-busy={loading}
      {...rest}
    >
      {loading && <Spinner size="sm" variant={variant === 'primary' && dark ? 'dark' : 'white'} aria-hidden />}
      {children}
    </button>
  );
});
